import { c } from 'ttag';

import { MembershipDisplayStatus } from '../../graphql/__generated__/schema';
import { PortalUserQuery } from '../../graphql/__generated__/types';
import { formatDate } from './display';
import { statusLabel } from './status';

type Membership = NonNullable<NonNullable<PortalUserQuery['portalUser']>['memberships']>[number];

/**
 * How the last closure of a membership is read.
 *
 * `revokedAt`, `revokedReason` and `revokedBy` describe the last closure, never the
 * current state: reopening does not clear them, so they stay filled on memberships that
 * are alive today. `status` is therefore read first, always, and the three fields reach
 * the screen only through one of the states below.
 */
export type ClosureState =
    /** No closure was ever recorded on this membership. */
    | { kind: 'none' }
    /** The access is closed **now**, and this is why. The reopening gesture applies. */
    | { kind: 'current'; at: string | null; reason: string | null; by: string | null }
    /**
     * A closure happened and is no longer what the access carries: reopened since,
     * or overtaken by another state. History, not a situation to act on.
     */
    | { kind: 'past'; at: string | null; reason: string | null; by: string | null };

export const closureStateOf = (membership: Membership): ClosureState => {
    const facts = {
        at: membership.revokedAt ?? null,
        reason: membership.revokedReason ?? null,
        by: membership.revokedBy ?? null,
    };
    // `status` first: it is the only one of the four fields that describes today.
    if (membership.status === MembershipDisplayStatus.AccessRevoked) {
        return { kind: 'current', ...facts };
    }
    if (!facts.at) {
        return { kind: 'none' };
    }
    return { kind: 'past', ...facts };
};

/**
 * Who closed it and when. The author is null once their own account has been deleted
 * (`ON DELETE SET NULL`), so a closure outlives its author.
 */
export const closureAuthorSentence = (state: ClosureState): string | null => {
    if (state.kind === 'none') {
        return null;
    }
    const date = formatDate(state.at);
    if (state.by) {
        const author = state.by;
        return c('Membership closure').t`Closed on ${date} by ${author}.`;
    }
    return c('Membership closure').t`Closed on ${date}. The account that decided it no longer exists.`;
};

/**
 * The motive, or the sentence that says a motive was never given. A null reason is shown
 * as a sentence rather than a blank, which would read as a field that failed to load.
 */
export const closureReasonSentence = (state: ClosureState): string | null => {
    if (state.kind === 'none') {
        return null;
    }
    return state.reason ?? c('Membership closure').t`No reason was recorded for this closure.`;
};

/**
 * What a past closure carries so it is not mistaken for the current situation. It states
 * what the access reads today rather than claiming the closure was undone: on a disabled
 * person the display status masks the stored one, so "reopened" would sometimes be a guess.
 */
export const closureIsHistorySentence = (state: ClosureState, membership: Membership): string | null => {
    if (state.kind !== 'past') {
        return null;
    }
    if (!membership.status) {
        return c('Membership closure').t`That is a past event, not the current state of this access.`;
    }
    const current = statusLabel(membership.status);
    return c('Membership closure').t`That is a past event: this access now reads « ${current} ».`;
};

/**
 * Whether the sheet offers to reopen this membership, and why the control is dead when
 * it is. A disabled row is the one case the payload cannot decide (`DISABLED` outranks
 * the stored status), so the control is offered but disabled with its reason. The other
 * refusals need facts this query does not carry and are rendered after the click.
 */
export interface ReinstateOffer {
    /** Whether the control is rendered at all. */
    offered: boolean;
    /** Non-null when it is rendered dead, with this as its reason. */
    blocker: string | null;
}

export const reinstateOfferOf = (membership: Membership, state: ClosureState): ReinstateOffer => {
    if (state.kind === 'current') {
        return { offered: true, blocker: null };
    }
    if (state.kind === 'past' && membership.status === MembershipDisplayStatus.Disabled) {
        return {
            offered: true,
            blocker: c('Membership closure')
                .t`This person's account is disabled, which masks the state of each of their accesses. Reactivate the account first: reopening would produce an access the portal keeps refusing.`,
        };
    }
    return { offered: false, blocker: null };
};

/**
 * The statuses a membership carries while it is open. `ACCESS_REVOKED`, `HAS_LEFT` and
 * `DISABLED` are excluded: for all three the closing gesture would act on something that
 * is not open.
 */
const OPEN_STATUSES: ReadonlySet<MembershipDisplayStatus> = new Set([
    MembershipDisplayStatus.Active,
    MembershipDisplayStatus.ActiveNeverLoggedIn,
    MembershipDisplayStatus.Invited,
    MembershipDisplayStatus.InvitationExpired,
]);

/**
 * Whether the sheet offers to close this membership. Offered only while the access is
 * open. The refusals closing can still meet need facts this query does not carry and are
 * read after the click.
 */
export const revokeOfferOf = (membership: Membership, state: ClosureState): boolean =>
    state.kind !== 'current' && !!membership.status && OPEN_STATUSES.has(membership.status);
